<template>
  <v-container
    class="pa-0"
    fluid
    data-cy="change-password-container"
  >
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <sn-label
          title="Current password"
          data-cy="current-password-field-label"
          class="pb-2"
        />
        <sn-password
          v-model="currentPassword"
          class="mb-4 sn-password"
          data-cy="current-password-field"
          sn-input-props_label="Current password"
          :sn-input-props_hint="currentPasswordHint"
          :sn-input-props_error-messages="currentPasswordError"
          @blur="enableCurrentPassValidation"
          @keyup="validateCurrentPassword"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <sn-label
          title="New password"
          data-cy="new-password-field-label"
          class="pb-2"
        />
        <sn-password
          v-model="newPassword"
          label="New password"
          class="mb-4 sn-password"
          data-cy="new-password-field"
          :sn-input-props_hint="newPasswordHint"
          :sn-input-props_error-messages="newPasswordError"
          @blur="enableNewPassValidation"
          @keyup="validateNewPassword"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <sn-label
          title="Confirm new password"
          data-cy="confirm-password-field-label"
          class="pb-2"
        />
        <sn-password
          v-model="confirmPassword"
          label="Confirm new password"
          class="mb-4 sn-password"
          data-cy="confirm-password-field"
          :sn-input-props_error-messages="confirmPasswordHint"
          @blur="enableConfirmPassValidation"
          @keyup="validateConfirmPassword"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <div class="d-flex">
          <sn-btn
            style="marginTop: 10px"
            text="Save"
            type="primary"
            size="large"
            data-cy="change-password-save-btn"
            :disabled="!saveEnabled"
            :loading="passwordSaveLoading"
            @click="handleSave"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import { updateUserPassword } from '../graphql/users/users_queries.gql'

import { mapMutations } from 'vuex'

export default {
  name: 'ChangePassword',
  props: {
    initiatorUserGuid: {
      type: String,
      default: '',
      required: true
    }
  },
  data () {
    return {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      currentPasswordHint: '',
      currentPasswordError: '',
      newPasswordHint: 'Password must have at least 9 characters, one uppercase letter, one lowercase letter, and one number',
      newPasswordError: '',
      confirmPasswordHint: '',
      currentPassValidationEnabled: false,
      newPassValidationEnabled: false,
      confirmPassValidationEnabled: false,
      currentPassIsValid: false,
      newPassIsValid: false,
      confirmPassIsValid: false,
      showSuccessToast: false,
      showErrorToast: false,
      successToastMessage: '',
      successToastDescription: '',
      errorToastMessage: '',
      passwordSaveLoading: false
    }
  },

  computed: {
    saveEnabled () {
      return this.currentPassIsValid && this.newPassIsValid && this.confirmPassIsValid
    }
  },

  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType']),
    validateCurrentPassword () {
      if (this.currentPassword) {
        this.$emit('change', true)
      } else {
        this.$emit('change', false)
      }
      if (this.currentPassValidationEnabled) {
        this.currentPassIsValid = !!this.currentPassword
        this.currentPasswordError = this.currentPassIsValid ? '' : this.currentPasswordError
        this.currentPasswordHint = ''
      }
    },
    validateNewPassword () {
      if (this.newPassword) {
        this.$emit('change', true)
      } else {
        this.$emit('change', false)
      }
      if (this.newPassValidationEnabled) {
        this.newPassIsValid = snValidationsService.password(this.newPassword)
        const newPassMessage = 'Password must have at least 9 characters, one uppercase letter, one lowercase letter, and one number'
        this.newPasswordError = this.newPassIsValid ? '' : newPassMessage
        this.newPasswordHint = this.newPassIsValid ? newPassMessage : ''
      }
      if (this.confirmPassValidationEnabled) {
        this.validateConfirmPassword()
      }
    },
    validateConfirmPassword () {
      if (this.confirmPassword) {
        this.$emit('change', true)
      } else {
        this.$emit('change', false)
      }
      if (!this.confirmPassValidationEnabled && this.confirmPassword.length > 8) {
        this.enableConfirmPassValidation()
      } else if (this.confirmPassValidationEnabled) {
        this.confirmPassIsValid = this.newPassword === this.confirmPassword
        this.confirmPasswordHint = this.confirmPassIsValid ? '' : 'Passwords do not match'
      }
    },
    enableCurrentPassValidation () {
      this.currentPassValidationEnabled = true
      this.validateCurrentPassword()
    },
    enableNewPassValidation () {
      this.newPassValidationEnabled = true
      this.validateNewPassword()
    },
    enableConfirmPassValidation () {
      this.confirmPassValidationEnabled = true
      this.validateConfirmPassword()
    },
    handleSave () {
      this.passwordSaveLoading = true
      this.$apollo
        .mutate({
          mutation: updateUserPassword,
          variables: {
            currentPassword: this.currentPassword,
            newPassword: this.newPassword,
            confirmPassword: this.confirmPassword,
            initiatorUserGuid: this.initiatorUserGuid
          }
        })
        .then(({ data }) => {
          const { message } = data.updateUserPassword
          if (message === 'Password Updated') {
            Object.assign(this.$data, this.$options.data())
            this.successToastMessage = 'Password updated'
            this.successToastDescription = 'You must login again. You will be redirected momentarily.'
            this.setSuccessToast({ message: this.successToastMessage, description: this.successToastDescription })
            this.$emit('save')
            setTimeout(() => { window.location.href = '/login' }, 4000)
          } else if (message === 'Incorrect current password') {
            this.passwordSaveLoading = false
            this.currentPassword = ''
            this.currentPasswordError = message
            this.currentPassIsValid = false
            this.errorToastMessage = message
            this.setErrorToastType({ description: this.errorToastMessage })
          } else if (message === 'Invalid User') {
            window.location.href = '/login'
          } else {
            this.passwordSaveLoading = false
            this.errorToastMessage = message
            this.showErrorToast = true
          }
        }).catch(e => {
        this.errorToastMessage = 'An error occurred while updating this information. Please try again later.'
        this.setErrorToastType({ description: this.errorToastMessage })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.illustration-container {
  background-color: $sn-brand-01;
  width: 112px;
  height: 112px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-row {
  width: 100%;
}

.sn-password {
  min-width: 50%;
  width: 528px;
  max-width: 100%;
}
</style>
